import { useEffect, useState } from "react";
import { ConfettiLogos } from "../components/ConfettiLogos";
import { ScratchCard } from "../components/ScratchCard";
import { events, initAmplitude } from "../lib/amplitude";
import { clearSession, loadSession } from "../lib/session";

/**
 * /complete — interview cleared the 30s pre-filter (architecture §5.2).
 * Fires the `interview_completed` conversion event and clears the
 * participant session so a refresh / back-button doesn't drop them back
 * into the call screen.
 *
 * Reward reveal (prototype §5.8): a ScratchCard hides the gift card; once
 * the participant scratches enough of it away, the ConfettiLogos firework
 * mounts and plays its one-shot burst.
 */
export function Complete() {
  const [revealed, setRevealed] = useState(false);
  // Reward eligibility set by endCall from the server's validity verdict.
  // "0" = completed_invalid → no reward (no scratch card). Default eligible
  // when the flag is absent (e.g. direct visit) so we never wrongly deny.
  const [eligible] = useState(
    () => sessionStorage.getItem("interview_reward") !== "0",
  );

  useEffect(() => {
    const session = loadSession();
    // Track ONLY for real identity participants: a Leaply user_id must be
    // present (and not a dev/QA session). No user_id → send nothing.
    if (session && !session.isTest && session.userId) {
      initAmplitude(session.userId);
      events.completed({
        studyId: session.studyId,
      });
    }
    clearSession();
    sessionStorage.removeItem("interview_reward");
  }, []);

  return (
    <>
      {/* Firework only mounts after the scratch card is fully scratched —
          mounting triggers its one-shot WAAPI burst. */}
      {eligible && revealed && <ConfettiLogos />}

      <div className="relative z-10 w-full max-w-[560px] flex flex-col items-center text-center gap-6 sm:gap-7">
        <h1 className="font-serif text-balance text-[28px] leading-[34px] sm:text-[36px] sm:leading-[44px] lg:text-[42px] lg:leading-[50px] text-surface-ink mx-auto max-w-[16ch]">
          Thank you for your time!
        </h1>

        {eligible ? (
          <>
            <p className="text-body-m text-surface-subtle max-w-[56ch] mx-auto px-2 leading-relaxed">
              Your reward will land in your inbox within 3 business days.
              Didn't get it? Reach out at{" "}
              <a
                href="mailto:support@theleaply.com"
                className="font-semibold text-brand-700 hover:underline whitespace-nowrap"
              >
                support@theleaply.com
              </a>
              .
            </p>
            <ScratchCard onReveal={() => setRevealed(true)} />
          </>
        ) : (
          // completed_invalid — thank them warmly, but no reward and no
          // scratch card. Kept gracious, not accusatory.
          <p className="text-body-m text-surface-subtle max-w-[56ch] mx-auto px-2 leading-relaxed">
            Thanks for chatting with Lea — your responses have been recorded.
            Questions? Reach out at{" "}
            <a
              href="mailto:support@theleaply.com"
              className="font-semibold text-brand-700 hover:underline whitespace-nowrap"
            >
              support@theleaply.com
            </a>
            .
          </p>
        )}
      </div>
    </>
  );
}
